/**
 * AdditionalTablesTab — F12.S T12.S.C.5 (Settings → Tabele dodatkowe).
 *
 * Zarządzanie definicjami tabel dodatkowych (1:N do kartoteki systemowej):
 *  - Master-detail: lewa kolumna = lista + filtr rodzica, prawa = panel detalu
 *  - Definicja: inline edit displayName / description / allowAddRow (PATCH)
 *  - Kolumny: inline edit nazwy pola / toggle isRequired (PATCH) / usunięcie (DELETE)
 *  - Tworzenie tabeli (POST) — picker rodziców: 6 kartotek systemowych (MVP)
 *  - Usuwanie tabeli (DELETE) — ConfirmDialog danger
 *
 * Gating: `crm_core.custom_modules.manage`.
 *
 * TODO: custom-module parents — parent picker w MVP tylko systemowe kartoteki;
 *       moduły własne jako rodzice odłożone do kolejnego etapu.
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.alert/confirm/prompt — ConfirmDialog + useToast.
 */

import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SYSTEM_EXT_FIELD_TYPE_SET,
  SYSTEM_EXT_FIELD_TYPES,
  type SystemExtFieldType,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Database, Plus, Trash2 } from 'lucide-react';

import { InlineEditableCell } from '@/components/additional/InlineEditableCell';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DictionaryParamBindingsEditor } from '@/components/dictionaries/DictionaryParamBindingsEditor';
import { DictionaryPicker } from '@/components/dictionaries/DictionaryPicker';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { systemFieldTypeI18nKey } from '@/components/system-modules/AddSystemFieldModal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { type DictionaryParamBindingsMap, useDictionary } from '@/hooks/useDictionary';
import {
  additionalFieldsKey,
  additionalRecordsTableKey,
  fetchAdditionalTableFields,
} from '@/lib/additional-tables/parent-field-values';
import type { AdditionalField } from '@/lib/additional-tables/types';
import { api } from '@/lib/api';
import {
  DEFAULT_REFERENCE_TARGET,
  REFERENCE_TARGET_OPTIONS,
  type ReferenceTargetSlug,
  toReferenceTargetSlug,
} from '@/lib/system-entity-adapters';

import {
  type AdditionalTableOwnerRef,
  useParentFieldBindingConfig,
} from './useParentFieldBindingConfig';

/** Typy pola dla których pokazujemy picker słownika (opcje listy pochodzą ze słownika). */
const DICTIONARY_CAPABLE_TYPES: ReadonlySet<SystemExtFieldType> = new Set([
  'select',
  'multiselect',
]);

// ─── Typy ─────────────────────────────────────────────────────────────────────

interface AdditionalTable {
  id: string;
  slug: string;
  displayName: string;
  displayNamePlural: string | null;
  parentTableName: string;
  parentEntityType: 'system' | 'custom';
  parentEntity: string;
  icon: string | null;
  description: string | null;
  allowAddRow: boolean;
  sortOrder: number | null;
  isActive: boolean;
}

/** Opcja rodzica (MVP — tylko kartoteki systemowe). */
interface ParentOption {
  labelKey: string;
  parentTableName: string;
  parentEntityType: 'system';
  parentEntity: string;
}

/** Whitelist kartotek systemowych jako rodziców tabel dodatkowych. */
const SYSTEM_PARENT_OPTIONS: ParentOption[] = [
  {
    labelKey: 'additionalTables.admin.parentOptions.employees',
    parentTableName: 'employees',
    parentEntityType: 'system',
    parentEntity: 'employees',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.contractors',
    parentTableName: 'contractors',
    parentEntityType: 'system',
    parentEntity: 'contractors',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.companies',
    parentTableName: 'companies',
    parentEntityType: 'system',
    parentEntity: 'companies',
  },
  {
    // F24 Pakiet 0: fizyczna tabela `departments` → `org_units` (jednostki organizacyjne).
    labelKey: 'additionalTables.admin.parentOptions.orgUnits',
    parentTableName: 'org_units',
    parentEntityType: 'system',
    parentEntity: 'org_units',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.contractorContacts',
    parentTableName: 'contractor_contacts',
    parentEntityType: 'system',
    parentEntity: 'contractor_contacts',
  },
  {
    labelKey: 'additionalTables.admin.parentOptions.users',
    parentTableName: 'users',
    parentEntityType: 'system',
    parentEntity: 'users',
  },
  {
    // Obszar Dokumenty (F20): jedna wspólna tabela dodatkowa widoczna
    // we wszystkich 7 modułach dokumentowych (Wariant A).
    labelKey: 'additionalTables.admin.parentOptions.documents',
    parentTableName: 'documents',
    parentEntityType: 'system',
    parentEntity: 'documents',
  },
];

/** Zwraca zlokalizowaną etykietę rodzica jeśli jest w SYSTEM_PARENT_OPTIONS, inaczej surowy slug. */
function parentLabel(t: ReturnType<typeof useTranslation>['t'], parentEntity: string): string {
  const opt = SYSTEM_PARENT_OPTIONS.find((o) => o.parentEntity === parentEntity);
  return opt ? t(opt.labelKey) : parentEntity;
}

const SLUG_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

function toSystemExtFieldType(s: string): SystemExtFieldType {
  return SYSTEM_EXT_FIELD_TYPE_SET.has(s) ? (s as SystemExtFieldType) : 'varchar';
}

function nameToSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 63) || 'tabela'
  );
}

// ─── Formularz tworzenia tabeli ─────────────────────────────────────────────

interface CreateTableFormState {
  displayName: string;
  slug: string;
  slugTouched: boolean;
  parentKey: string; // 'employees' | 'contractors' | ...
  allowAddRow: boolean;
}

function emptyCreateTableForm(): CreateTableFormState {
  return {
    displayName: '',
    slug: '',
    slugTouched: false,
    parentKey: SYSTEM_PARENT_OPTIONS[0]!.parentEntity,
    allowAddRow: true,
  };
}

interface CreateTableModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: {
    displayName: string;
    slug: string;
    parentTableName: string;
    parentEntityType: 'system';
    parentEntity: string;
    allowAddRow: boolean;
  }) => void;
  saving: boolean;
}

function CreateTableModal({ open, onClose, onSubmit, saving }: CreateTableModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateTableFormState>(emptyCreateTableForm());

  useEffect(() => {
    if (!open) setForm(emptyCreateTableForm());
  }, [open]);

  const resolvedSlug = form.slugTouched ? form.slug : nameToSlug(form.displayName);
  const slugError =
    resolvedSlug && !SLUG_REGEX.test(resolvedSlug)
      ? t('additionalTables.admin.validation.slugInvalid')
      : '';
  const displayNameError = !form.displayName.trim()
    ? t('additionalTables.admin.validation.displayNameRequired')
    : '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (displayNameError || slugError) return;
    const parent = SYSTEM_PARENT_OPTIONS.find((p) => p.parentEntity === form.parentKey);
    if (!parent) return;
    onSubmit({
      displayName: form.displayName.trim(),
      slug: resolvedSlug,
      parentTableName: parent.parentTableName,
      parentEntityType: parent.parentEntityType,
      parentEntity: parent.parentEntity,
      allowAddRow: form.allowAddRow,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setForm(emptyCreateTableForm());
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => handleOpenChange(false)}
      title={t('additionalTables.admin.createModal.title')}
      guardUnsaved
      // W trakcie zapisu nie da się zamknąć okna tłem ani Esc (jak „Anuluj").
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        data-testid="create-additional-table-form"
      >
        <FormField
          label={t('additionalTables.admin.createModal.displayNameLabel')}
          error={form.displayName.trim() === '' && form.slugTouched ? displayNameError : ''}
          required
        >
          <Input
            value={form.displayName}
            onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
            placeholder={t('additionalTables.admin.createModal.displayNamePlaceholder')}
            data-testid="create-table-display-name"
            autoFocus
          />
        </FormField>

        <FormField
          label={t('additionalTables.admin.createModal.slugLabel')}
          hint={t('additionalTables.admin.createModal.slugHint')}
          error={slugError}
        >
          <Input
            value={resolvedSlug}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
            }
            placeholder={t('additionalTables.admin.createModal.slugPlaceholder')}
            data-testid="create-table-slug"
          />
        </FormField>

        <FormField label={t('additionalTables.admin.createModal.parentLabel')} required>
          {/* TODO: custom-module parents */}
          <Dropdown
            value={form.parentKey}
            onChange={(val) => setForm((prev) => ({ ...prev, parentKey: val }))}
            options={SYSTEM_PARENT_OPTIONS.map((opt) => ({
              value: opt.parentEntity,
              label: t(opt.labelKey),
            }))}
            aria-label={t('additionalTables.admin.createModal.parentLabel')}
            data-testid="create-table-parent"
          />
        </FormField>

        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={form.allowAddRow}
            onChange={(e) => setForm((prev) => ({ ...prev, allowAddRow: e.target.checked }))}
            className="rounded border-border-primary"
            data-testid="create-table-allow-add-row"
          />
          {t('additionalTables.admin.createModal.allowAddRowLabel')}
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !form.displayName.trim()}
            data-testid="create-table-submit"
          >
            {saving ? t('common.saving') : t('additionalTables.admin.createModal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Formularz dodawania pola ───────────────────────────────────────────────

interface AddFieldFormState {
  name: string;
  slug: string;
  slugTouched: boolean;
  fieldType: SystemExtFieldType;
  isRequired: boolean;
  dictionaryCode: string | null;
  /** Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   *  `null` = użyj `dictionary.displayMode` (fallback). Ma sens tylko gdy
   *  `dictionaryCode` jest ustawiony. */
  dictionaryDisplayMode: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (Etap 10.2/10.5, ADR-0066 §11; Faza 2b
   * ADR-0068 — tagowane: pole rekordu / firma / user / stała). `null` = brak wiązań.
   */
  dictionaryParamBindings: DictionaryParamBindingsMap | null;
  /** Cel odwołania — sensowny tylko gdy `fieldType === 'reference'`; NULL poza tym. */
  relationSystemModuleSlug: ReferenceTargetSlug | null;
}

function emptyAddFieldForm(): AddFieldFormState {
  return {
    name: '',
    slug: '',
    slugTouched: false,
    fieldType: 'varchar',
    isRequired: false,
    dictionaryCode: null,
    dictionaryDisplayMode: null,
    dictionaryParamBindings: null,
    relationSystemModuleSlug: null,
  };
}

interface AddFieldPanelProps {
  tableId: string;
  /** Właściciel tabeli — rozstrzyga, czy wiązanie „Pole kartoteki" jest dostępne i z jakich pól. */
  owner: AdditionalTableOwnerRef;
  /** Pola już istniejące w tej tabeli — kandydaci do wiązania parametrów `record_field`. */
  siblingFields: AdditionalField[];
  onAdded: () => void;
}

function AddFieldPanel({ tableId, owner, siblingFields, onAdded }: AddFieldPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AddFieldFormState>(emptyAddFieldForm());
  const [visible, setVisible] = useState(false);
  const fieldTypeSelectId = useId();

  const resolvedSlug = form.slugTouched ? form.slug : nameToSlug(form.name);

  // Szczegół wybranego słownika (params — deklaracje parametrów dla widgetu wiązań).
  const dictionaryDetail = useDictionary(
    DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode ? form.dictionaryCode : '',
  );
  // Review finding (pre-commit Etapu 10.5): `DictionaryParamBindingsEditor` zwraca
  // `null` gdy słownik nie deklaruje żadnego parametru (przypadek częsty — słowniki
  // ręczne albo zewnętrzne bez parametrów) — bez tej bramki sizing wrapper
  // `sm:w-1/2 sm:min-w-[280px]` renderował się i tak, zostawiając pusty box.
  // Siostrzane edytory (`AddSystemFieldModal`, `AddKartotekaFieldModal`,
  // `ModuleTemplateEditor`) takiego wrappera nie mają. Od Fazy 2b (model
  // dwuetapowy) widget obejmuje WSZYSTKIE zadeklarowane parametry, nie tylko
  // dawne `record_field` — bramka liczy więc samą obecność deklaracji.
  const hasDeclaredParams = (dictionaryDetail.dictionary?.params ?? []).length > 0;

  // Rodzaj wiązania „Pole kartoteki" (wartość z pola rekordu-rodzica) — katalogi pól właściciela
  // pobieramy dopiero, gdy edytor wiązań jest faktycznie na ekranie.
  const parentField = useParentFieldBindingConfig(
    owner,
    visible &&
      DICTIONARY_CAPABLE_TYPES.has(form.fieldType) &&
      form.dictionaryCode !== null &&
      hasDeclaredParams,
  );

  const addMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      slug: string;
      fieldType: string;
      isRequired: boolean;
      dictionaryCode: string | null;
      dictionaryDisplayMode: 'label' | 'code' | 'code_label' | null;
      dictionaryParamBindings: DictionaryParamBindingsMap | null;
      relationSystemModuleSlug?: ReferenceTargetSlug;
    }) => {
      await api.post(`/settings/additional-tables/${tableId}/fields`, payload);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.fieldAdded') });
      setForm(emptyAddFieldForm());
      setVisible(false);
      queryClient.invalidateQueries({ queryKey: additionalFieldsKey(tableId) });
      onAdded();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      if (code === 'FIELD_SLUG_DUPLICATE') {
        toast.show({
          variant: 'warning',
          title: t('additionalTables.admin.toast.fieldSlugDuplicate'),
        });
        return;
      }
      if (code === 'MISSING_RELATION_TARGET') {
        toast.show({
          variant: 'warning',
          title: t('additionalTables.admin.toast.missingRelationTarget'),
        });
        return;
      }
      if (code === 'INVALID_RELATION_TARGET') {
        toast.show({
          variant: 'warning',
          title: t('additionalTables.admin.toast.invalidRelationTarget'),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.fieldAddError') });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Druga linia obrony — przycisk zapisu jest już disabled, gdy nazwa jest pusta.
    if (!form.name.trim()) {
      toast.show({
        variant: 'error',
        title: t('additionalTables.admin.validation.displayNameRequired'),
      });
      return;
    }
    if (!SLUG_REGEX.test(resolvedSlug)) {
      toast.show({ variant: 'error', title: t('additionalTables.admin.validation.slugInvalid') });
      return;
    }
    // Cel odwołania wysyłamy WYŁĄCZNIE dla typu `reference`; bez wybranego celu nie ma
    // zapisu — żadnego cichego fallbacku na domyślną kartotekę (przycisk zapisu jest w
    // tym stanie i tak zablokowany, ta bramka to druga linia obrony).
    const referenceTarget = form.fieldType === 'reference' ? form.relationSystemModuleSlug : null;
    if (form.fieldType === 'reference' && !referenceTarget) {
      toast.show({
        variant: 'error',
        title: t('additionalTables.admin.toast.missingRelationTarget'),
      });
      return;
    }
    addMutation.mutate({
      name: form.name.trim(),
      slug: resolvedSlug,
      fieldType: form.fieldType,
      isRequired: form.isRequired,
      dictionaryCode: DICTIONARY_CAPABLE_TYPES.has(form.fieldType) ? form.dictionaryCode : null,
      dictionaryDisplayMode:
        DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode
          ? form.dictionaryDisplayMode
          : null,
      dictionaryParamBindings:
        DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && form.dictionaryCode
          ? form.dictionaryParamBindings
          : null,
      ...(referenceTarget ? { relationSystemModuleSlug: referenceTarget } : {}),
    });
  };

  if (!visible) {
    return (
      <button
        type="button"
        onClick={() => setVisible(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:border-accent-500 hover:text-accent-500"
        data-testid={`additional-add-field-btn-${tableId}`}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        {t('additionalTables.admin.addField.btn')}
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="@container rounded-lg border border-border-primary bg-bg-secondary p-3"
      data-testid={`additional-add-field-form-${tableId}`}
    >
      <div className="grid grid-cols-1 gap-3 @xl:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {t('additionalTables.admin.addField.nameLabel')}
          </label>
          <Input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('additionalTables.admin.addField.namePlaceholder')}
            data-testid={`additional-field-name-${tableId}`}
            autoFocus
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {t('additionalTables.admin.addField.slugLabel')}
          </label>
          <Input
            value={resolvedSlug}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
            }
            placeholder={t('additionalTables.admin.addField.slugPlaceholder')}
            data-testid={`additional-field-slug-${tableId}`}
          />
        </div>
        <div>
          {/* Etykieta zostaje surowym <label>, ale związanym przez `htmlFor` z `id` kontrolki —
              prop `label` FormFielda renderuje ją w `text-sm`, co rozjechałoby wysokości
              w tym czterokolumnowym wierszu (sąsiednie pola mają etykiety `text-xs`). */}
          <label
            htmlFor={fieldTypeSelectId}
            className="mb-1 block text-xs font-medium text-text-secondary"
          >
            {t('additionalTables.admin.addField.typeLabel')}
          </label>
          <Dropdown
            id={fieldTypeSelectId}
            value={form.fieldType}
            onChange={(v) => {
              const nextType = toSystemExtFieldType(v);
              setForm((prev) => ({
                ...prev,
                fieldType: nextType,
                // Auto-select przy przełączeniu NA reference — zachowuje ręczny wybór,
                // gdy user wcześniej już przełączał typ tam i z powrotem (mirror
                // AddSystemFieldModal.tsx).
                relationSystemModuleSlug:
                  nextType === 'reference'
                    ? (prev.relationSystemModuleSlug ?? DEFAULT_REFERENCE_TARGET)
                    : prev.relationSystemModuleSlug,
              }));
            }}
            options={SYSTEM_EXT_FIELD_TYPES.map((ft) => ({
              value: ft,
              label: t(systemFieldTypeI18nKey(ft), { defaultValue: ft }),
            }))}
            data-testid={`additional-field-type-${tableId}`}
          />
        </div>
        <div className="flex flex-col justify-end gap-2">
          <label className="flex items-center gap-1.5 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={(e) => setForm((prev) => ({ ...prev, isRequired: e.target.checked }))}
              className="rounded border-border-primary"
              data-testid={`additional-field-required-${tableId}`}
            />
            {t('additionalTables.admin.addField.requiredLabel')}
          </label>
          <div className="flex gap-1.5">
            <Button
              type="submit"
              variant="primary"
              disabled={
                !form.name.trim() ||
                addMutation.isPending ||
                (form.fieldType === 'reference' && !form.relationSystemModuleSlug)
              }
              data-testid={`additional-field-submit-${tableId}`}
            >
              {t('common.add')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setForm(emptyAddFieldForm());
                setVisible(false);
              }}
              disabled={addMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </div>

      {form.fieldType === 'reference' && (
        <div className="mt-3 @xl:w-1/4 @xl:min-w-[220px]">
          {/* Etykieta z propa `label` — <FormField> renderuje <label htmlFor> powiązany
              z triggerem Dropdownu, więc nie dublujemy jej osobnym, niepowiązanym elementem. */}
          <FormField label={t('common.referenceTarget.label')}>
            <Dropdown
              value={form.relationSystemModuleSlug ?? ''}
              onChange={(v) =>
                setForm((prev) => ({
                  ...prev,
                  relationSystemModuleSlug: toReferenceTargetSlug(v),
                }))
              }
              options={REFERENCE_TARGET_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
              }))}
              data-testid={`additional-field-reference-target-${tableId}`}
            />
          </FormField>
        </div>
      )}

      {DICTIONARY_CAPABLE_TYPES.has(form.fieldType) && (
        <div className="mt-3 flex flex-col gap-3 @xl:flex-row">
          <div className="@xl:w-1/4 @xl:min-w-[220px]">
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              {t('common.dictionaryPicker.label')}
            </label>
            <DictionaryPicker
              value={form.dictionaryCode}
              onChange={(code) =>
                setForm((prev) => ({
                  ...prev,
                  dictionaryCode: code,
                  dictionaryParamBindings: null,
                }))
              }
              data-testid={`additional-field-dictionary-${tableId}`}
            />
          </div>
          {form.dictionaryCode && (
            <div className="@xl:w-1/4 @xl:min-w-[220px]">
              <label className="mb-1 block text-xs font-medium text-text-secondary">
                {t('common.dictionaryDisplayModeOverride.label')}
              </label>
              <Dropdown
                value={form.dictionaryDisplayMode ?? ''}
                onChange={(v) =>
                  setForm((prev) => ({
                    ...prev,
                    dictionaryDisplayMode: (v ||
                      null) as AddFieldFormState['dictionaryDisplayMode'],
                  }))
                }
                data-testid={`additional-field-dictionary-display-mode-${tableId}`}
                options={[
                  {
                    value: '',
                    label: t('common.dictionaryDisplayModeOverride.useDictionaryDefault'),
                  },
                  {
                    value: 'label',
                    label: t('common.dictionaryDisplayModeOverride.options.label'),
                  },
                  { value: 'code', label: t('common.dictionaryDisplayModeOverride.options.code') },
                  {
                    value: 'code_label',
                    label: t('common.dictionaryDisplayModeOverride.options.code_label'),
                  },
                ]}
              />
            </div>
          )}
          {form.dictionaryCode && hasDeclaredParams && (
            <div className="@xl:w-1/2 @xl:min-w-[280px]">
              <DictionaryParamBindingsEditor
                params={dictionaryDetail.dictionary?.params}
                siblingFields={siblingFields.map((f) => ({ slug: f.slug, name: f.name }))}
                recordFieldLabel={t('additionalTables.admin.addField.recordFieldKind')}
                parentField={parentField}
                value={form.dictionaryParamBindings}
                onChange={(bindings) =>
                  setForm((prev) => ({ ...prev, dictionaryParamBindings: bindings }))
                }
                data-testid={`additional-field-param-bindings-${tableId}`}
              />
            </div>
          )}
        </div>
      )}
    </form>
  );
}

// ─── Panel detalu tabeli (prawa kolumna) ─────────────────────────────────────

interface TableDetailPanelProps {
  table: AdditionalTable;
  canManage: boolean;
  onDeleted: () => void;
}

function TableDetailPanel({ table, canManage, onDeleted }: TableDetailPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pendingDeleteField, setPendingDeleteField] = useState<AdditionalField | null>(null);
  /**
   * Flow potwierdzenia zmiany celu na kolumnie z danymi (decyzja #4) — backend jest źródłem
   * prawdy o potrzebie potwierdzenia: pierwszy PATCH idzie bez `confirmClearExistingValues`,
   * a 409 `RELATION_TARGET_CHANGE_REQUIRES_CONFIRMATION` (z `affectedRows`) otwiera ten
   * dialog zamiast generycznego toastu błędu. Select w międzyczasie wraca do poprzedniej
   * wartości sam — `value` czyta się z `fields` (query cache), która na 409 zostaje nietknięta.
   *
   * Trzymamy SAMO `fieldId` (nie cały rekord pola) i bierzemy je z `variables` mutacji —
   * odpowiedź 409 dotycząca pola, którego nie ma już w cache listy (np. odświeżonej w
   * międzyczasie), musi otworzyć ten dialog, a nie degradować się do generycznego błędu.
   * Czyszczenie stanu robią callbacki mutacji (`onSuccess`/`onError`), NIE handler
   * `onConfirm` — inaczej kasowalibyśmy kontekst zanim żądanie się rozstrzygnie.
   */
  const [pendingRetarget, setPendingRetarget] = useState<{
    fieldId: string;
    newTarget: ReferenceTargetSlug;
    affectedRows: number;
  } | null>(null);

  // ─── Pola tabeli ────────────────────────────────────────────────────────────

  // Ten sam klucz i ta sama funkcja pobierania co zakładka na karcie rekordu i edytor układu
  // kolumn w kreatorze: jedna kopia pól, więc zmiana pola tutaj odświeża też otwarte karty
  // i kreator (także w schowanych kartach powłoki, które zostają zamontowane).
  const fieldsQuery = useQuery({
    queryKey: additionalFieldsKey(table.id),
    queryFn: () => fetchAdditionalTableFields(table.id),
  });

  // ─── PATCH definicji tabeli ─────────────────────────────────────────────────

  const patchTableMutation = useMutation({
    mutationFn: async (
      body: Partial<Pick<AdditionalTable, 'displayName' | 'description' | 'allowAddRow'>>,
    ) => {
      await api.patch(`/settings/additional-tables/${table.id}`, body);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.tableSaved') });
      queryClient.invalidateQueries({ queryKey: ['additional-tables'] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.tableSaveError') });
    },
  });

  // ─── PATCH pola ─────────────────────────────────────────────────────────────

  const patchFieldMutation = useMutation({
    mutationFn: async ({
      fieldId,
      body,
    }: {
      fieldId: string;
      body: Partial<Pick<AdditionalField, 'name' | 'isRequired' | 'relationSystemModuleSlug'>> & {
        confirmClearExistingValues?: boolean;
      };
    }) => {
      await api.patch(`/settings/additional-tables/${table.id}/fields/${fieldId}`, body);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.fieldSaved') });
      // Zamykamy dialog potwierdzenia dopiero gdy PATCH faktycznie przeszedł (dla zapisów
      // niezwiązanych z celem stan i tak jest już `null`, więc to no-op).
      setPendingRetarget(null);
      queryClient.invalidateQueries({ queryKey: additionalFieldsKey(table.id) });
      // Zmiana pola (np. celu odwołania, nazwy) zmienia też to, co pokazują zapamiętane wiersze
      // otwartych kart — prefiks obejmuje wszystkie rekordy-rodziców tej tabeli.
      queryClient.invalidateQueries({ queryKey: additionalRecordsTableKey(table.id) });
    },
    onError: (err, variables) => {
      const requestedTarget = variables.body.relationSystemModuleSlug;
      if (isAxiosError(err) && err.response?.status === 409) {
        const data = err.response.data as { error?: string; affectedRows?: number } | undefined;
        if (
          data?.error === 'RELATION_TARGET_CHANGE_REQUIRES_CONFIRMATION' &&
          typeof data.affectedRows === 'number' &&
          typeof requestedTarget === 'string'
        ) {
          // Parametry dialogu z `variables` żądania, nie z cache listy pól — 409 dla pola
          // nieobecnego w (potencjalnie nieaktualnym) cache też musi otworzyć dialog.
          setPendingRetarget({
            fieldId: variables.fieldId,
            newTarget: requestedTarget,
            affectedRows: data.affectedRows,
          });
          return;
        }
      }
      setPendingRetarget(null);
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.fieldSaveError') });
    },
  });

  // ─── DELETE pola ────────────────────────────────────────────────────────────

  const deleteFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      await api.delete(`/settings/additional-tables/${table.id}/fields/${fieldId}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.fieldDeleted') });
      setPendingDeleteField(null);
      queryClient.invalidateQueries({ queryKey: additionalFieldsKey(table.id) });
      // Usunięte pole znika też z zapamiętanych wierszy otwartych kart (wszyscy rodzice tabeli).
      queryClient.invalidateQueries({ queryKey: additionalRecordsTableKey(table.id) });
      queryClient.invalidateQueries({ queryKey: ['additional-tables'] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.fieldDeleteError') });
      setPendingDeleteField(null);
    },
  });

  const fields = fieldsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid={`additional-fields-panel-${table.id}`}>
      {/* ── Parametry podstawowe ─────────────────────────────────────────── */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
          {t('additionalTables.admin.detail.sectionParams')}
        </h3>

        <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
          {/* Kod (read-only) */}
          <span className="text-text-secondary">
            {t('additionalTables.admin.detail.labelCode')}
          </span>
          <span className="font-mono text-xs text-text-tertiary">{table.slug}</span>

          {/* Nazwa wyświetlana (inline-edit) */}
          <span className="text-text-secondary">
            {t('additionalTables.admin.detail.labelName')}
          </span>
          {canManage ? (
            <InlineEditableCell
              value={table.displayName}
              onCommit={(newVal) => patchTableMutation.mutate({ displayName: newVal })}
              renderInput={(draft, onChange, onKeyDown, onBlur, ref) => (
                <input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  value={draft}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  onBlur={onBlur}
                  className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                />
              )}
              renderDisplay={(v) => <span className="font-medium text-text-primary">{v}</span>}
              data-testid="additional-def-name"
            />
          ) : (
            <span className="font-medium text-text-primary">{table.displayName}</span>
          )}

          {/* Opis (inline-edit) */}
          <span className="text-text-secondary">
            {t('additionalTables.admin.detail.labelDescription')}
          </span>
          {canManage ? (
            <InlineEditableCell
              value={table.description ?? ''}
              onCommit={(newVal) =>
                patchTableMutation.mutate({
                  description: (newVal || null) as string | null,
                })
              }
              renderInput={(draft, onChange, onKeyDown, onBlur, ref) => (
                <input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  value={draft}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  onBlur={onBlur}
                  className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                />
              )}
              renderDisplay={(v) =>
                v ? (
                  <span className="text-text-secondary">{v}</span>
                ) : (
                  <span className="text-text-muted italic">
                    {t('additionalTables.admin.detail.descriptionPlaceholder')}
                  </span>
                )
              }
              data-testid="additional-def-description"
            />
          ) : (
            <span className="text-text-secondary">
              {table.description ?? <span className="text-text-muted">—</span>}
            </span>
          )}
        </div>

        {/* allowAddRow toggle */}
        <label className="mt-3 flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={table.allowAddRow}
            disabled={!canManage}
            onChange={(e) => {
              if (canManage) patchTableMutation.mutate({ allowAddRow: e.target.checked });
            }}
            className="rounded border-border-primary disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="additional-allow-add-row-toggle"
          />
          {t('additionalTables.admin.detail.labelAllowAddRow')}
        </label>
      </section>

      {/* ── Kolumny ──────────────────────────────────────────────────────── */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
          {t('additionalTables.admin.detail.sectionColumns')}
        </h3>

        {fieldsQuery.isLoading && (
          <div className="flex justify-center py-6" data-testid="additional-fields-loading">
            <Spinner />
          </div>
        )}

        {fieldsQuery.isError && (
          <p className="text-sm text-error-500">
            {t('additionalTables.admin.toast.fieldsLoadError')}
          </p>
        )}

        {!fieldsQuery.isLoading && !fieldsQuery.isError && (
          <>
            {fields.length === 0 ? (
              <p className="mb-3 text-sm text-text-tertiary" data-testid="additional-fields-empty">
                {t('additionalTables.admin.fields.empty')}
              </p>
            ) : (
              <Card padding="none" className="mb-3 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        {t('additionalTables.admin.detail.colCode')}
                      </th>
                      <th className="px-3 py-2 text-left">
                        {t('additionalTables.admin.detail.colDisplayName')}
                      </th>
                      <th className="px-3 py-2 text-left">
                        {t('additionalTables.admin.fields.colType')}
                      </th>
                      <th className="px-3 py-2 text-center">
                        {t('additionalTables.admin.fields.colRequired')}
                      </th>
                      {canManage && <th className="w-10 px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-primary">
                    {fields.map((f) => (
                      <tr
                        key={f.id}
                        className="hover:bg-bg-tertiary/50"
                        data-testid={`additional-field-row-${f.slug}`}
                      >
                        {/* Kod (slug) — read-only */}
                        <td className="px-3 py-2 font-mono text-xs text-text-tertiary">{f.slug}</td>

                        {/* Nazwa wyświetlana — inline-edit */}
                        <td className="px-3 py-2">
                          {canManage ? (
                            <InlineEditableCell
                              value={f.name}
                              onCommit={(newVal) =>
                                patchFieldMutation.mutate({ fieldId: f.id, body: { name: newVal } })
                              }
                              renderInput={(draft, onChange, onKeyDown, onBlur, ref) => (
                                <input
                                  ref={ref as React.RefObject<HTMLInputElement>}
                                  value={draft}
                                  onChange={(e) => onChange(e.target.value)}
                                  onKeyDown={onKeyDown}
                                  onBlur={onBlur}
                                  className="w-full rounded border border-accent-500 bg-bg-primary px-2 py-0.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
                                />
                              )}
                              renderDisplay={(v) => (
                                <span className="font-medium text-text-primary">{v}</span>
                              )}
                              data-testid={`additional-field-name-${f.id}`}
                            />
                          ) : (
                            <span className="font-medium text-text-primary">{f.name}</span>
                          )}
                        </td>

                        {/* Typ — read-only; dla reference dodatkowo selektor celu (decyzja #3) */}
                        <td className="px-3 py-2 text-text-secondary">
                          {t(systemFieldTypeI18nKey(toSystemExtFieldType(f.fieldType)), {
                            defaultValue: f.fieldType,
                          })}
                          {f.fieldType === 'reference' && (
                            <Dropdown
                              value={f.relationSystemModuleSlug ?? ''}
                              // Etykieta wizualna nie istnieje (selektor siedzi w komórce
                              // „Typ") — czytnik ekranu dostaje ją przez aria-label z nazwą
                              // pola, żeby odróżnić selektory poszczególnych wierszy.
                              aria-label={t('common.referenceTarget.ariaLabelFor', {
                                field: f.name,
                              })}
                              // Blokada na czas zapisu — bez tego kolejny wybór wystartowałby
                              // drugi PATCH, zanim pierwszy się rozstrzygnie (a dla zmiany
                              // celu pierwszy może dopiero wrócić z prośbą o potwierdzenie).
                              disabled={!canManage || patchFieldMutation.isPending}
                              onChange={(v) => {
                                const nextTarget = toReferenceTargetSlug(v);
                                if (canManage && nextTarget) {
                                  patchFieldMutation.mutate({
                                    fieldId: f.id,
                                    body: { relationSystemModuleSlug: nextTarget },
                                  });
                                }
                              }}
                              // Opcja pusta/disabled TYLKO do uczciwego pokazania legacy kolumny
                              // bez celu — nigdy nie da się jej wybrać z powrotem (front nigdy
                              // nie wysyła null).
                              options={
                                f.relationSystemModuleSlug
                                  ? REFERENCE_TARGET_OPTIONS.map((opt) => ({
                                      value: opt.value,
                                      label: t(opt.labelKey),
                                    }))
                                  : [
                                      {
                                        value: '',
                                        label: t('common.referenceTarget.placeholder'),
                                        disabled: true,
                                      },
                                      ...REFERENCE_TARGET_OPTIONS.map((opt) => ({
                                        value: opt.value,
                                        label: t(opt.labelKey),
                                      })),
                                    ]
                              }
                              className="mt-1"
                              data-testid={`additional-field-reference-target-${f.id}`}
                            />
                          )}
                        </td>

                        {/* Wymagane — checkbox (immediate PATCH) */}
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={f.isRequired}
                            disabled={!canManage}
                            onChange={(e) => {
                              if (canManage)
                                patchFieldMutation.mutate({
                                  fieldId: f.id,
                                  body: { isRequired: e.target.checked },
                                });
                            }}
                            className="rounded border-border-primary disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid={`additional-field-required-${f.id}`}
                          />
                        </td>

                        {/* Akcje */}
                        {canManage && (
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => setPendingDeleteField(f)}
                              className="rounded p-1 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                              aria-label={t('common.delete')}
                              data-testid={`additional-field-delete-${f.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}

            {canManage && (
              <AddFieldPanel
                tableId={table.id}
                owner={table}
                siblingFields={fields}
                onAdded={() => queryClient.invalidateQueries({ queryKey: ['additional-tables'] })}
              />
            )}
          </>
        )}
      </section>

      {/* ConfirmDialog: usuń pole */}
      <ConfirmDialog
        open={pendingDeleteField !== null}
        variant="danger"
        title={t('additionalTables.admin.confirmDeleteField.title')}
        message={
          pendingDeleteField
            ? t('additionalTables.admin.confirmDeleteField.message', {
                name: pendingDeleteField.name,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteField) deleteFieldMutation.mutate(pendingDeleteField.id);
        }}
        onClose={() => setPendingDeleteField(null)}
      />

      {/* ConfirmDialog: zmiana celu odwołania na kolumnie z danymi (decyzja #4) */}
      <ConfirmDialog
        open={pendingRetarget !== null}
        variant="danger"
        title={t('additionalTables.admin.confirmRetarget.title')}
        message={
          pendingRetarget
            ? t('additionalTables.admin.confirmRetarget.message', {
                count: pendingRetarget.affectedRows,
              })
            : ''
        }
        confirmLabel={t('additionalTables.admin.confirmRetarget.confirm')}
        confirming={patchFieldMutation.isPending}
        onConfirm={() => {
          if (!pendingRetarget) return;
          // BEZ `setPendingRetarget(null)` tutaj — stan czyszczą callbacki mutacji, więc
          // dialog zostaje (z zablokowanymi przyciskami) aż PATCH się rozstrzygnie.
          patchFieldMutation.mutate({
            fieldId: pendingRetarget.fieldId,
            body: {
              relationSystemModuleSlug: pendingRetarget.newTarget,
              confirmClearExistingValues: true,
            },
          });
        }}
        onClose={() => setPendingRetarget(null)}
      />

      {/* Przycisk usuń tabelę (w panelu detalu) */}
      {canManage && (
        <div className="pt-2 border-t border-border-primary">
          <Button
            variant="secondary"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={onDeleted}
            data-testid={`additional-table-delete-detail-${table.id}`}
            className="text-red-600 hover:text-red-700"
          >
            {t('additionalTables.admin.detail.deleteTable')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Główny komponent ─────────────────────────────────────────────────────────

export function AdditionalTablesTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isSuperUser = me?.user.isSuperUser === true;
  const canManage =
    isSuperUser || (me?.permissions.includes('crm_core.custom_modules.manage') ?? false);

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDeleteTable, setPendingDeleteTable] = useState<AdditionalTable | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [parentFilter, setParentFilter] = useState<string>(''); // '' = all

  const tablesQuery = useQuery({
    queryKey: ['additional-tables'],
    queryFn: async () => {
      const res = await api.get<{ tables: AdditionalTable[] }>('/settings/additional-tables');
      return res.data.tables;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: {
      displayName: string;
      slug: string;
      parentTableName: string;
      parentEntityType: 'system';
      parentEntity: string;
      allowAddRow: boolean;
    }) => {
      const res = await api.post<{ table: AdditionalTable }>('/settings/additional-tables', body);
      return res.data.table;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['additional-tables'] });
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.tableCreated') });
      setCreateOpen(false);
      setSelectedTableId(created.id);
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data?.error as string | undefined)
        : undefined;
      if (code === 'SATELLITE_TABLE_LIMIT_EXCEEDED') {
        toast.show({ variant: 'warning', title: t('additionalTables.admin.toast.limitExceeded') });
        return;
      }
      if (code === 'SATELLITE_SLUG_DUPLICATE') {
        toast.show({ variant: 'warning', title: t('additionalTables.admin.toast.slugDuplicate') });
        return;
      }
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.createError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/settings/additional-tables/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['additional-tables'] });
      toast.show({ variant: 'success', title: t('additionalTables.admin.toast.tableDeleted') });
      setPendingDeleteTable(null);
      // Deselect if the deleted table was selected — use the authoritative mutation argument
      setSelectedTableId((prev) => (prev === id ? null : prev));
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('additionalTables.admin.toast.deleteError') });
      setPendingDeleteTable(null);
    },
  });

  const tables = tablesQuery.data ?? [];

  const filteredTables =
    parentFilter === '' ? tables : tables.filter((tbl) => tbl.parentEntity === parentFilter);

  const selectedTable =
    selectedTableId != null ? (tables.find((tbl) => tbl.id === selectedTableId) ?? null) : null;

  // If selected table no longer in filtered list, keep it selected (it still exists in `tables`)
  // but show it in the detail panel regardless.

  return (
    <div data-testid="additional-tables-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('additionalTables.admin.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('additionalTables.admin.subtitle')}</p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
            data-testid="additional-tables-new-btn"
          >
            {t('additionalTables.admin.newTable')}
          </Button>
        )}
      </header>

      {tablesQuery.isLoading && (
        <div className="flex justify-center py-10" data-testid="additional-tables-loading">
          <Spinner />
        </div>
      )}

      {tablesQuery.isError && (
        <Card padding="md">
          <p className="text-sm text-error-500">{t('additionalTables.admin.loadError')}</p>
        </Card>
      )}

      {!tablesQuery.isLoading && !tablesQuery.isError && (
        <div className="flex gap-4">
          {/* ── Lewa kolumna: lista + filtr ─────────────────────────────── */}
          <div className="w-72 flex-shrink-0">
            {/* Filtr rodzica */}
            <div className="mb-3">
              <Dropdown
                value={parentFilter}
                onChange={(v) => setParentFilter(v)}
                data-testid="additional-tables-filter"
                options={[
                  { value: '', label: t('additionalTables.admin.filter.all') },
                  ...SYSTEM_PARENT_OPTIONS.map((opt) => ({
                    value: opt.parentEntity,
                    label: t(opt.labelKey),
                  })),
                ]}
              />
            </div>

            {filteredTables.length === 0 ? (
              <Card padding="md" data-testid="additional-tables-empty">
                <p className="text-sm text-text-tertiary">{t('additionalTables.admin.empty')}</p>
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border-primary">
                  {filteredTables.map((tbl) => (
                    <li key={tbl.id} data-testid={`additional-table-row-${tbl.id}`}>
                      <div
                        className={`flex items-center justify-between px-3 py-2.5 hover:bg-bg-tertiary/50 ${
                          selectedTableId === tbl.id ? 'bg-bg-tertiary' : ''
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTableId((prev) => (prev === tbl.id ? null : tbl.id))
                          }
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          data-testid={`additional-table-manage-fields-${tbl.id}`}
                        >
                          <Database
                            className="h-4 w-4 flex-shrink-0 text-text-muted"
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-text-primary">
                              {tbl.displayName}
                            </div>
                            <div className="truncate text-xs text-text-tertiary">
                              {parentLabel(t, tbl.parentEntity)}
                            </div>
                          </div>
                        </button>
                        {canManage && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDeleteTable(tbl);
                            }}
                            className="ml-1 rounded p-1 text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            aria-label={t('common.delete')}
                            data-testid={`additional-table-delete-${tbl.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* ── Prawa kolumna: detal ─────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {selectedTable == null ? (
              <Card padding="md">
                <p className="text-sm text-text-tertiary">
                  {t('additionalTables.admin.detail.selectPrompt')}
                </p>
              </Card>
            ) : (
              <Card padding="md">
                <header className="mb-4 flex items-center gap-2">
                  <Database className="h-5 w-5 text-text-muted" aria-hidden="true" />
                  {selectedTable.isActive ? (
                    <Badge color="accent">{t('additionalTables.admin.activeLabel')}</Badge>
                  ) : (
                    <Badge color="gray">{t('additionalTables.admin.inactiveLabel')}</Badge>
                  )}
                </header>

                <TableDetailPanel
                  key={selectedTable.id}
                  table={selectedTable}
                  canManage={canManage}
                  onDeleted={() => setPendingDeleteTable(selectedTable)}
                />
              </Card>
            )}
          </div>
        </div>
      )}

      <CreateTableModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(values) => createMutation.mutate(values)}
        saving={createMutation.isPending}
      />

      <ConfirmDialog
        open={pendingDeleteTable !== null}
        variant="danger"
        title={t('additionalTables.admin.confirmDelete.title')}
        message={
          pendingDeleteTable
            ? t('additionalTables.admin.confirmDelete.message', {
                name: pendingDeleteTable.displayName,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteTable) deleteMutation.mutate(pendingDeleteTable.id);
        }}
        onClose={() => setPendingDeleteTable(null)}
      />
    </div>
  );
}
